import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { StatusBadge } from '../components/StatusBadge';
import { SignatureRequestPanel } from '../components/SignatureRequestPanel';
import { LeadAssignPanel } from '../components/LeadAssignPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  ChevronLeft,
  Loader2,
  Phone,
  CalendarClock,
  Save,
  ArrowRightCircle,
  PhoneCall,
  Mail,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Inbox,
  AlertTriangle,
  Link2,
  Search as SearchIcon,
  Trophy,
  FileText,
  Unlock,
  Clock,
  ShieldCheck,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';
import { ClientPageButton } from '../components/client360/c360Shared';
import { LeadResumo360 } from '../components/LeadResumo360';
import { LeadDocumentsPanel } from '../components/LeadDocumentsPanel';
import { LeadRestricoesPanel } from '../components/LeadRestricoesPanel';
import { LeadDataDialog } from '../components/LeadDataDialog';
import { restricoesResumo, empresaLabel, RESTRICAO_TIPOS } from '../lib/restricoes';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SOURCE_LABELS = {
  call_center: 'Call Center', indicacao: 'Indicação', listagem: 'Listagem',
  landing_page: 'Landing Page', meu_cliente: 'Meu Cliente', outro: 'Outro',
};

const OUTCOME_LABELS = {
  atendeu: 'Atendeu', nao_atende: 'Não Atende', ocupado: 'Ocupado',
  voicemail: 'Voicemail', reagendar: 'Reagendar', sem_interesse: 'Sem Interesse',
  convertido: 'Convertido', outro: 'Outro',
};

const METHOD_LABELS = {
  telefone: 'Telefone', email: 'Email', reuniao: 'Reunião', outro: 'Outro',
};

const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), "dd MMM yyyy 'às' HH:mm", { locale: pt }); } catch { return iso; }
};

export const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [lead, setLead] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  // New contact form
  const [outcome, setOutcome] = useState('atendeu');
  const [method, setMethod] = useState('telefone');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  // Reschedule dialog
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState('');
  const [reschedNotes, setReschedNotes] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  // Convert dialog state
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertCategory, setConvertCategory] = useState('luz_gas');
  const [convertCompany, setConvertCompany] = useState('');
  const [companies, setCompanies] = useState({});
  const [companiesLoading, setCompaniesLoading] = useState(false);

  // Shared-pool (claim/decline) state
  const [accepting, setAccepting] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);

  // Associate existing contract dialog state
  const [associateOpen, setAssociateOpen] = useState(false);
  const [associateQuery, setAssociateQuery] = useState('');
  const [associateResults, setAssociateResults] = useState([]);
  const [associateSearching, setAssociateSearching] = useState(false);
  const [associateContractId, setAssociateContractId] = useState('');
  const [associateReason, setAssociateReason] = useState('');
  const [associating, setAssociating] = useState(false);

  const CATEGORY_LABELS = {
    luz_gas: 'Luz / Gás',
    solar: 'Solar',
    telecomunicacoes: 'Telecomunicações',
    carregamento: 'Carregamento Elétrico',
    alarmes: 'Alarmes',
  };

  const [dataOpen, setDataOpen] = useState(false);
  const fetchData = useCallback(async () => {
    try {
      const [leadRes, contactsRes] = await Promise.all([
        axios.get(`${API_URL}/leads/${id}`),
        axios.get(`${API_URL}/leads/${id}/contacts`),
      ]);
      setLead(leadRes.data);
      setContacts(contactsRes.data || []);
    } catch (err) {
      toast.error('Erro ao carregar lead');
      navigate('/leads/pipeline');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const h = () => fetchData();
    window.addEventListener('crm:refresh', h);
    return () => window.removeEventListener('crm:refresh', h);
  }, [fetchData]);

  const goBackToList = useCallback(() => {
    const r = location.state?.returnTo;
    if (window.history.length > 1) navigate(-1);
    else if (r) navigate(r);
    else navigate('/leads/pipeline');
  }, [location.state?.returnTo, navigate]);

  const submitContact = async (e) => {
    e.preventDefault();
    setSavingContact(true);
    try {
      const payload = { outcome, method, notes };
      if (outcome === 'reagendar' && nextAction) payload.next_action_at = new Date(nextAction).toISOString();
      await axios.post(`${API_URL}/leads/${id}/contact`, payload);
      toast.success('Contacto registado', {
        action: { label: 'Voltar à lista', onClick: goBackToList },
        duration: 6000,
      });
      setOutcome('atendeu'); setMethod('telefone'); setNotes(''); setNextAction('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao registar contacto');
    } finally {
      setSavingContact(false);
    }
  };

  const submitReschedule = async () => {
    if (!reschedDate) { toast.error('Escolha data e hora'); return; }
    setRescheduling(true);
    try {
      await axios.post(`${API_URL}/leads/${id}/reschedule`, {
        next_action_at: new Date(reschedDate).toISOString(),
        notes: reschedNotes,
      });
      toast.success('Reagendado', {
        action: { label: 'Voltar à lista', onClick: goBackToList },
        duration: 6000,
      });
      setRescheduleOpen(false);
      setReschedDate(''); setReschedNotes('');
      fetchData();
    } catch (err) {
      toast.error('Erro ao reagendar');
    } finally {
      setRescheduling(false);
    }
  };

  const openConvertDialog = async () => {
    setConvertOpen(true);
    setCompaniesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/config/companies/${convertCategory}`);
      setCompanies(res.data || {});
      const firstKey = Object.keys(res.data || {})[0];
      setConvertCompany(firstKey || '');
    } catch {
      setCompanies({});
    } finally {
      setCompaniesLoading(false);
    }
  };

  // Reload companies when category changes inside the dialog
  useEffect(() => {
    if (!convertOpen) return;
    setCompaniesLoading(true);
    axios.get(`${API_URL}/config/companies/${convertCategory}`)
      .then(res => {
        setCompanies(res.data || {});
        const firstKey = Object.keys(res.data || {})[0];
        setConvertCompany(firstKey || '');
      })
      .catch(() => setCompanies({}))
      .finally(() => setCompaniesLoading(false));
  }, [convertCategory, convertOpen]);

  const confirmConvert = async () => {
    if (!convertCompany) { toast.error('Escolha uma empresa'); return; }
    const hit = (lead?.restricoes || []).find((r) => convertCompany.toLowerCase().includes(r.empresa) || r.empresa.includes(convertCompany.toLowerCase()));
    if (hit && !window.confirm(`Atenção: este cliente tem ${RESTRICAO_TIPOS[hit.tipo] || 'Dívida'} na ${empresaLabel(hit.empresa)}${hit.nota ? ` (${hit.nota})` : ''}. Quer continuar mesmo assim?`)) return;
    try {
      const res = await axios.post(`${API_URL}/leads/${id}/convert-prefill`);
      sessionStorage.setItem('lead_prefill', JSON.stringify({ ...res.data.prefill, lead_id: id }));
      const w = res.data?.warnings || {};
      if (w.cpe_invalid) toast.warning('CPE do ficheiro importado tem formato inválido — preenche manualmente');
      if (w.cui_invalid) toast.warning('CUI do ficheiro importado tem formato inválido — preenche manualmente');
      toast.success('A abrir formulário de contrato pré-preenchido...');
      setConvertOpen(false);
      navigate(`/contracts/${convertCategory}/${convertCompany}/new${lead?.entrada_direta ? '?comercializadora_anterior=primeira_contratacao' : ''}`);
    } catch {
      toast.error('Erro ao converter');
    }
  };

  const convert = openConvertDialog; // alias kept for the button below

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API_URL}/leads/${id}/accept`);
      toast.success('Lead aceite com sucesso — agora é tua!');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao aceitar lead');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    const reason = declineReason.trim();
    if (reason.length < 10) {
      toast.error('A razão deve ter pelo menos 10 caracteres');
      return;
    }
    setDeclining(true);
    try {
      const res = await axios.post(`${API_URL}/leads/${id}/decline`, { reason });
      if (res.data.all_declined) {
        toast.success('Lead descartada — super_admins foram alertados.');
      } else {
        toast.success('Lead descartada');
      }
      setDeclineOpen(false);
      setDeclineReason('');
      navigate('/leads/pipeline');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao descartar lead');
    } finally {
      setDeclining(false);
    }
  };

  // Search existing contracts (debounced)
  useEffect(() => {
    if (!associateOpen) return;
    const q = (associateQuery || '').trim();
    if (q.length < 2) {
      setAssociateResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setAssociateSearching(true);
      try {
        const res = await axios.get(`${API_URL}/contracts`, { params: { search: q } });
        setAssociateResults((res.data || []).slice(0, 20));
      } catch {
        setAssociateResults([]);
      } finally {
        setAssociateSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [associateQuery, associateOpen]);

  const handleAssociateContract = async () => {
    if (!associateContractId) {
      toast.error('Escolha um contrato');
      return;
    }
    if ((associateReason || '').trim().length < 10) {
      toast.error('A razão deve ter pelo menos 10 caracteres');
      return;
    }
    setAssociating(true);
    try {
      await axios.post(`${API_URL}/leads/${id}/associate-contract`, {
        contract_id: associateContractId,
        reason: associateReason.trim(),
      });
      toast.success('Contrato associado à lead com sucesso');
      setAssociateOpen(false);
      setAssociateContractId('');
      setAssociateReason('');
      setAssociateQuery('');
      setAssociateResults([]);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao associar contrato');
    } finally {
      setAssociating(false);
    }
  };

  const isSharedPool = !!(lead && lead.shared_pool && !lead.pool_locked);
  const alreadyDeclined = !!(lead && user && (lead.declined_by || []).some(d => d.user_id === user.id));
  const isConverted = lead?.status === 'convertida';
  const canReopen = ['super_admin', 'gestor'].includes(user?.role);
  const [forceContact, setForceContact] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);

  const handleReopenLead = async () => {
    setReopening(true);
    try {
      await axios.post(`${API_URL}/leads/${id}/reopen`, { reason: reopenReason });
      toast.success('Lead reaberta — pode continuar o seguimento');
      setReopenOpen(false);
      setReopenReason('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao reabrir lead');
    } finally {
      setReopening(false);
    }
  };

  // Build correct contract URL (/contracts/:category/:company/:id) using enrichment data
  const gotoContract = () => {
    if (!lead?.contract_id) return;
    const cat = lead.contract_category;
    const comp = lead.contract_company;
    if (cat && comp) {
      navigate(`/contracts/${encodeURIComponent(cat)}/${encodeURIComponent(comp)}/${lead.contract_id}`);
    } else {
      // Fallback: navigate to all contracts list (avoid wrong /contracts/{id} URL)
      navigate(`/contracts/all?q=${encodeURIComponent(lead.contract_codigo || lead.contract_id)}`);
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;
  }
  if (!lead) return null;

  const outcomeIcon = (o) => {
    const i = { telefone: PhoneCall, email: Mail, reuniao: MessageSquare, outro: MessageSquare };
    return i[o] || MessageSquare;
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl" data-testid="lead-detail-page">
        {/* Header */}
        <div className="flex items-start gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => {
            const r = location.state?.returnTo;
            if (window.history.length > 1) navigate(-1);
            else if (r) navigate(r);
            else navigate('/leads/pipeline');
          }} data-testid="back-btn" className="gap-1.5">
            <ChevronLeft className="w-4 h-4" />
            Voltar à lista
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900 font-outfit truncate" data-testid="lead-name">{lead.client_name}</h1>
              <ClientPageButton nif={lead.client_nif} name={lead.client_name} leadId={lead.id} testId="lead-detail-client-360" />
              {lead.service_type && (
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    lead.service_type === 'dual' ? 'bg-indigo-100 text-indigo-800 border border-indigo-200' :
                    lead.service_type === 'gas' ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                    'bg-amber-100 text-amber-800 border border-amber-200'
                  }`}
                  data-testid={`service-type-${lead.service_type}`}
                  title={`Tipo de serviço inferido a partir do ficheiro importado (${lead.service_type === 'dual' ? 'CPE + CUI' : lead.service_type === 'gas' ? 'apenas CUI' : 'apenas CPE'})`}
                >
                  {lead.service_type === 'dual' && <>✨ Luz + Gás</>}
                  {lead.service_type === 'gas' && <>🔥 Gás</>}
                  {lead.service_type === 'luz' && <>⚡ Luz</>}
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-4 flex-wrap">
              {lead.client_phone && (
                <a href={`tel:${lead.client_phone}`} className="text-2xl font-bold text-[#2D3E6F] hover:underline font-mono flex items-center gap-2" data-testid="lead-phone-main">
                  📱 {lead.client_phone}
                </a>
              )}
              {lead.client_phone_alt && (
                <a href={`tel:${lead.client_phone_alt}`} className="text-sm text-slate-500 hover:text-slate-700 font-mono" data-testid="lead-phone-alt">
                  ☎️ {lead.client_phone_alt} (fixo)
                </a>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-3 flex-wrap">
              {lead.client_email && <span>📧 {lead.client_email}</span>}
              {lead.client_nif && <span>NIF {lead.client_nif}</span>}
              {lead.client_locality && <span>📍 {lead.client_locality}</span>}
              {lead.has_private_data && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-medium" data-testid="private-data-badge" title="Esta lead foi importada com dados sensíveis (CPE, morada, NIF completo). Os dados serão revelados ao converter em Contrato.">
                  🔒 Dados de importação
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={lead.status} />
            {isSharedPool ? (
              <>
                <Button
                  onClick={handleAccept}
                  disabled={accepting || alreadyDeclined}
                  className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2 text-white"
                  data-testid="accept-lead-btn"
                >
                  {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Aceitar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeclineOpen(true)}
                  disabled={alreadyDeclined}
                  className="rounded-full gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
                  data-testid="decline-lead-btn"
                >
                  <XCircle className="w-4 h-4" />
                  {alreadyDeclined ? 'Já descartaste' : 'Descartar'}
                </Button>
              </>
            ) : (
              <>
                {!isConverted && (
                  <>
                    <Button variant="outline" onClick={() => setRescheduleOpen(true)} className="rounded-full gap-2" data-testid="reschedule-btn">
                      <CalendarClock className="w-4 h-4" />
                      Reagendar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setAssociateOpen(true)}
                      className="rounded-full gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      data-testid="associate-contract-btn"
                      title="Associar um contrato já criado a esta lead"
                    >
                      <Link2 className="w-4 h-4" />
                      Associar Contrato Existente
                    </Button>
                    <Button onClick={convert} className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2" data-testid="convert-btn">
                      <ArrowRightCircle className="w-4 h-4" />
                      Converter em Contrato
                    </Button>
                  </>
                )}
                {isConverted && lead.contract_id && (
                  <Button
                    onClick={gotoContract}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
                    data-testid="goto-contract-btn"
                  >
                    <FileText className="w-4 h-4" />
                    Ver Contrato
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <LeadResumo360 leadId={lead.id} clientName={lead.client_name} refreshKey={`${lead.status}-${lead.assigned_to}-${lead.updated_at}`} />

        {/* Converted lead banner — visually impossible to miss */}
        {isConverted && (
          <div
            className="rounded-2xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 p-5 shadow-sm"
            data-testid="converted-lead-banner"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div className="p-3 rounded-xl bg-emerald-100 shrink-0">
                <Trophy className="w-7 h-7 text-emerald-700" />
              </div>
              <div className="flex-1 min-w-[260px]">
                <h3 className="text-lg font-bold text-emerald-900 flex items-center gap-2 flex-wrap">
                  Lead convertida em Contrato 🎉
                  {lead.contract_codigo && (
                    <span className="text-xs font-mono bg-white border border-emerald-300 text-emerald-800 px-2 py-0.5 rounded">
                      {lead.contract_codigo}
                    </span>
                  )}
                </h3>
                <p className="text-sm text-emerald-800 mt-1">
                  {lead.contract_company && <><b>{lead.contract_company}</b> · </>}
                  {lead.contract_category || '—'}
                  {lead.contract_days_since != null && ` · há ${lead.contract_days_since} dias`}
                </p>

                {/* D+45 + Chargeback chips */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {lead.contract_d45_days_left != null && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        lead.contract_d45_days_left < 0
                          ? 'bg-rose-50 border-rose-200 text-rose-800'
                          : lead.contract_d45_days_left <= 7
                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                            : 'bg-white border-emerald-200 text-emerald-800'
                      }`}
                      data-testid="d45-chip"
                      title="Check-in obrigatório 45 dias após ativação do contrato"
                    >
                      <Clock className="w-3.5 h-3.5" />
                      {lead.contract_d45_days_left < 0
                        ? `D+45 em atraso (${-lead.contract_d45_days_left}d)`
                        : lead.contract_d45_days_left === 0
                          ? 'D+45 hoje!'
                          : `D+45 em ${lead.contract_d45_days_left}d`}
                    </span>
                  )}
                  {lead.contract_chargeback_days > 0 && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        lead.contract_chargeback_passed
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                          : 'bg-amber-50 border-amber-200 text-amber-800'
                      }`}
                      data-testid="chargeback-chip"
                      title="Período em que a comercializadora pode anular o contrato (chargeback)"
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                      {lead.contract_chargeback_passed
                        ? 'Chargeback ultrapassado ✓'
                        : `Chargeback: ${lead.contract_chargeback_days_left}d / ${lead.contract_chargeback_days}d`}
                    </span>
                  )}
                  {lead.contract_status && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 border border-slate-200 text-slate-700">
                      Estado: {lead.contract_status}
                    </span>
                  )}
                </div>

                <p className="text-[12px] text-emerald-700 mt-3 italic">
                  Esta lead já foi convertida. Para continuar a tratar do cliente, abre o contrato.
                  {!canReopen && ' Se precisares de reabrir a lead, contacta o gestor.'}
                </p>
              </div>

              <div className="flex flex-col gap-2 shrink-0">
                {lead.contract_id && (
                  <Button
                    onClick={gotoContract}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
                    data-testid="banner-goto-contract-btn"
                  >
                    <FileText className="w-4 h-4" />
                    Abrir Contrato
                  </Button>
                )}
                {canReopen && (
                  <Button
                    variant="outline"
                    onClick={() => setReopenOpen(true)}
                    className="rounded-full gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="reopen-lead-btn"
                  >
                    <Unlock className="w-4 h-4" />
                    Reabrir Lead
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Shared-pool banner */}
        {isSharedPool && (
          <div
            className="rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-rose-50 p-4 flex items-start gap-3"
            data-testid="shared-pool-banner"
          >
            <div className="p-2 rounded-xl bg-amber-100">
              <Inbox className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">Lead em pool partilhado — primeiro a aceitar ganha</p>
              <p className="text-xs text-amber-800 mt-1">
                Esta lead foi submetida pelo parceiro angariador e está disponível para {lead.eligible_owner_ids?.length || 0} utilizador(es) elegíveis.
                Descartes: {(lead.declined_by || []).length}.
              </p>
              {alreadyDeclined && (
                <p className="text-xs font-medium text-rose-700 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Já descartaste esta lead.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Register contact + thread */}
          <div className="lg:col-span-2 space-y-6">
            {/* Private Data panel — admin/gestor only */}
            {lead.private_data && (
              <Card className="rounded-2xl border-amber-200 bg-amber-50/30" data-testid="private-data-panel">
                <CardHeader className="border-b border-amber-200 pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-900">
                    🔒 Dados de Importação (apenas Admin/Gestor)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    {lead.private_data.nif_full && (
                      <div className="bg-white border rounded p-2"><div className="text-[10px] text-slate-500">NIF</div><div className="font-mono font-semibold">{lead.private_data.nif_full}</div></div>
                    )}
                    {lead.private_data.cpe && (
                      <div className="bg-white border rounded p-2"><div className="text-[10px] text-slate-500">CPE</div><div className="font-mono text-xs">{lead.private_data.cpe}</div></div>
                    )}
                    {lead.private_data.cui && (
                      <div className="bg-white border rounded p-2"><div className="text-[10px] text-slate-500">CUI</div><div className="font-mono text-xs">{lead.private_data.cui}</div></div>
                    )}
                    {lead.private_data.morada && (
                      <div className="bg-white border rounded p-2 col-span-2 md:col-span-3"><div className="text-[10px] text-slate-500">Morada</div><div>{lead.private_data.morada}</div></div>
                    )}
                    {lead.private_data.codigo_postal && (
                      <div className="bg-white border rounded p-2"><div className="text-[10px] text-slate-500">Cód. Postal</div><div className="font-mono">{lead.private_data.codigo_postal}</div></div>
                    )}
                    {lead.private_data.freguesia && (
                      <div className="bg-white border rounded p-2"><div className="text-[10px] text-slate-500">Freguesia</div><div>{lead.private_data.freguesia}</div></div>
                    )}
                    {lead.private_data.concelho && (
                      <div className="bg-white border rounded p-2"><div className="text-[10px] text-slate-500">Concelho</div><div>{lead.private_data.concelho}</div></div>
                    )}
                    {lead.private_data.distrito && (
                      <div className="bg-white border rounded p-2"><div className="text-[10px] text-slate-500">Distrito</div><div>{lead.private_data.distrito}</div></div>
                    )}
                  </div>
                  <p className="text-[11px] text-amber-700 mt-3 italic">⚠️ Estes dados não são visíveis ao comercial. Aparecerão automaticamente no contrato quando este converter a lead.</p>
                </CardContent>
              </Card>
            )}
            {/* Privacy notice for non-admin when lead has private data */}
            {!lead.private_data && lead.has_private_data && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600" data-testid="private-data-notice">
                🔒 Esta lead tem dados de importação adicionais (CPE, morada, NIF) que serão revelados automaticamente quando converteres em <b>Contrato</b>.
              </div>
            )}

            {/* Register contact */}
            <Card className={`rounded-2xl border-slate-100 ${isConverted && !forceContact ? 'opacity-60' : ''}`}>
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#2D3E6F]" /> Registar Contacto
                  {isConverted && (
                    <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                      LEAD CONVERTIDA
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {isConverted && !forceContact ? (
                  <div className="text-center py-8" data-testid="contact-blocked-notice">
                    <Trophy className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-600 mb-2">Esta lead já foi convertida. Os follow-ups devem ser feitos a partir do contrato.</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setForceContact(true)}
                      className="text-xs gap-1.5"
                      data-testid="force-contact-btn"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Registar mesmo assim
                    </Button>
                  </div>
                ) : (
                <form onSubmit={submitContact} className="space-y-3" data-testid="register-contact-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Resultado *</Label>
                      <Select value={outcome} onValueChange={setOutcome}>
                        <SelectTrigger data-testid="contact-outcome"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(OUTCOME_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Método</Label>
                      <Select value={method} onValueChange={setMethod}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(METHOD_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {outcome === 'reagendar' && (
                    <div>
                      <Label>Próximo contacto *</Label>
                      <Input
                        type="datetime-local"
                        value={nextAction}
                        onChange={(e) => setNextAction(e.target.value)}
                        required
                        data-testid="contact-next-action"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Notas</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="O que disse o cliente, condições, observações..."
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" disabled={savingContact} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2" data-testid="submit-contact">
                      {savingContact ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Registar
                    </Button>
                  </div>
                </form>
                )}
              </CardContent>
            </Card>

            {/* Thread */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Histórico de Contactos ({contacts.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4" data-testid="contact-thread">
                {contacts.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Sem contactos ainda. Registe o primeiro acima.</p>
                ) : contacts.map(c => {
                  const Icon = outcomeIcon(c.method);
                  const isPositive = ['convertido'].includes(c.outcome);
                  const isNegative = ['sem_interesse', 'nao_atende', 'voicemail'].includes(c.outcome);
                  return (
                    <div key={c.id} className="flex gap-3" data-testid={`contact-${c.id}`}>
                      <Avatar className="h-9 w-9 flex-shrink-0">
                        <AvatarFallback className="bg-[#2D3E6F] text-white text-xs font-semibold">
                          {initials(c.created_by_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-semibold text-slate-800">{c.created_by_name}</span>
                          <span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isPositive ? 'bg-emerald-100 text-emerald-700' : isNegative ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
                            <Icon className="inline w-3 h-3 mr-1 -mt-0.5" />
                            {OUTCOME_LABELS[c.outcome] || c.outcome}
                          </span>
                        </div>
                        {c.notes && <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.notes}</p>}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Right column: meta */}
          <div className="space-y-6">
            <LeadAssignPanel
              leadId={lead.id}
              userRole={user?.role}
              currentAssignedName={lead.assigned_to_name}
              currentAssignedId={lead.assigned_to}
              isConverted={lead.status === 'convertida'}
              onAssigned={(updated) => { if (updated) setLead((prev) => ({ ...prev, ...updated })); else fetchData(); }}
            />
            {user?.role !== 'angariador' && (
              <SignatureRequestPanel leadId={lead.id} clientPhone={lead.client_phone} clientName={lead.client_name} />
            )}
            <LeadRestricoesPanel lead={lead} onSaved={(r) => setLead((prev) => ({ ...prev, restricoes: r }))} />
            <LeadDocumentsPanel leadId={lead.id} />
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
                <CardTitle className="text-base font-semibold">Detalhes</CardTitle>
                {user?.role !== 'angariador' && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/chat?ref_type=lead&ref_id=${lead.id}&ref_label=${encodeURIComponent(lead.client_name || 'Lead')}&ref_url=${encodeURIComponent(`/leads/${lead.id}`)}`)} data-testid="lead-chat-btn">Falar sobre esta lead</Button>
                    <Button size="sm" variant="outline" onClick={() => setDataOpen(true)} data-testid="lead-complete-data-btn">Completar / Editar dados</Button>
                  </div>
                )}
              </CardHeader>
              <LeadDataDialog leadId={lead.id} open={dataOpen} onClose={() => setDataOpen(false)} onSaved={fetchData} />
              <CardContent className="p-6 space-y-3 text-sm">
                <div>
                  <p className="text-xs text-slate-400 mb-1">Origem</p>
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                    {SOURCE_LABELS[lead.source] || 'Outro'}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Próxima ação</p>
                  <p className="font-medium text-slate-700">{fmtDate(lead.next_action_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Último contacto</p>
                  <p className="font-medium text-slate-700">{fmtDate(lead.last_contact_at)}</p>
                </div>
                {lead.service_interest && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Interesse</p>
                    <p className="font-medium text-slate-700">{lead.service_interest}</p>
                  </div>
                )}
                {(lead.client_address || lead.client_postal_code || lead.client_locality) && (
                  <div data-testid="lead-detail-morada">
                    <p className="text-xs text-slate-400 mb-1">Morada</p>
                    <p className="font-medium text-slate-700">
                      {[lead.client_address, lead.client_door && `Nº ${lead.client_door}`, lead.client_floor, lead.client_side].filter(Boolean).join(', ')}
                    </p>
                    <p className="text-slate-600">{[lead.client_postal_code, lead.client_locality].filter(Boolean).join(' ')}</p>
                  </div>
                )}
                {lead.notes && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Notas iniciais</p>
                    <p className="text-slate-700 text-sm whitespace-pre-wrap">{lead.notes}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-400 mb-1">Atribuída a</p>
                  <p className="font-medium text-slate-700">{lead.assigned_to_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Carregada em</p>
                  <p className="text-slate-600 text-xs" data-testid="lead-detail-loaded">{fmtDate(lead.created_at)}</p>
                  {(lead.contact_attempts === 0 || !lead.contact_attempts) && lead.status === 'nova' && lead.created_at && Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 86400000) >= 2 && (
                    <span className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                      ⏳ Sem ação há {Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 86400000)} dias
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Histórico anterior (apenas para leads "Meu Cliente") */}
            {lead.source === 'meu_cliente' && (
              <Card className="rounded-2xl border-indigo-200 bg-indigo-50/30" data-testid="meu-cliente-card">
                <CardHeader className="border-b border-indigo-100">
                  <CardTitle className="text-base font-semibold text-indigo-800 flex items-center gap-2">
                    ✨ Histórico Anterior
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-3 text-sm">
                  {lead.previous_commercializer_name && (
                    <div>
                      <p className="text-xs text-indigo-600 mb-1">Comercializadora Anterior</p>
                      <p className="font-semibold text-slate-800">{lead.previous_commercializer_name}</p>
                    </div>
                  )}
                  {lead.previous_contract_date && (
                    <div>
                      <p className="text-xs text-indigo-600 mb-1">Data do último contrato</p>
                      <p className="font-medium text-slate-700">{fmtDate(lead.previous_contract_date)}</p>
                    </div>
                  )}
                  {lead.previous_contract_file_id ? (
                    <div>
                      <p className="text-xs text-indigo-600 mb-1">Contrato anterior</p>
                      <a
                        href={`${API_URL}/files/${lead.previous_contract_file_id}/download?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-indigo-700 hover:text-indigo-900 font-medium underline"
                        data-testid="previous-contract-download"
                      >
                        📎 {lead.previous_contract_filename || 'Descarregar PDF'}
                      </a>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">Sem PDF do contrato anterior anexado.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Reschedule dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle>Reagendar Contacto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Data e hora *</Label>
              <Input type="datetime-local" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} data-testid="resched-date" />
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea value={reschedNotes} onChange={(e) => setReschedNotes(e.target.value)} rows={3} placeholder="Razão do reagendamento..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleOpen(false)}>Cancelar</Button>
            <Button onClick={submitReschedule} disabled={rescheduling} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="resched-submit">
              {rescheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle>Converter Lead em Contrato</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Escolhe o tipo de serviço e a empresa para abrir o formulário pré-preenchido com os dados desta lead.
            </p>
            <div>
              <Label className="text-sm">Tipo de Serviço *</Label>
              <Select value={convertCategory} onValueChange={setConvertCategory}>
                <SelectTrigger data-testid="convert-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Empresa *</Label>
              {companiesLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> A carregar empresas...
                </div>
              ) : Object.keys(companies).length === 0 ? (
                <p className="text-sm text-slate-400 py-2">Sem empresas configuradas para esta categoria.</p>
              ) : (
                <Select value={convertCompany} onValueChange={setConvertCompany}>
                  <SelectTrigger data-testid="convert-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(companies).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.name || k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
              ✓ O formulário vai abrir com nome, telefone, email, NIF, morada e observações pré-preenchidos.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)}>Cancelar</Button>
            <Button
              onClick={confirmConvert}
              disabled={companiesLoading || !convertCompany}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="confirm-convert-btn"
            >
              <ArrowRightCircle className="w-4 h-4" />
              Avançar para Contrato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Associate existing contract dialog */}
      <Dialog open={associateOpen} onOpenChange={setAssociateOpen}>
        <DialogContent className="max-w-2xl" data-testid="associate-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-indigo-600" />
              Associar Contrato Existente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Use esta opção quando o contrato já foi criado mas não passou pelo fluxo de conversão da lead. A lead passará a <strong>"Convertida"</strong> e o angariador desta lead será copiado para o contrato.
            </p>
            <div>
              <Label className="text-sm">Pesquisar contrato</Label>
              <div className="relative mt-1">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Código, nome do cliente, NIF..."
                  value={associateQuery}
                  onChange={(e) => { setAssociateQuery(e.target.value); setAssociateContractId(''); }}
                  className="pl-10"
                  data-testid="associate-search-input"
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto border border-slate-100 rounded-lg" data-testid="associate-results-list">
              {associateSearching ? (
                <div className="flex items-center justify-center py-6 text-sm text-slate-400 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> A pesquisar...
                </div>
              ) : associateResults.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">
                  {associateQuery.trim().length < 2 ? 'Digite pelo menos 2 caracteres' : 'Nenhum contrato encontrado'}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {associateResults.map(c => (
                    <li
                      key={c.id}
                      className={`px-3 py-2 cursor-pointer hover:bg-slate-50 ${associateContractId === c.id ? 'bg-indigo-50 ring-1 ring-indigo-300' : ''}`}
                      onClick={() => setAssociateContractId(c.id)}
                      data-testid={`associate-result-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            #{c.codigo} · {c.nome_cliente}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {c.company_name || c.company} · NIF {c.nif || '—'} · Estado: {c.status}
                          </p>
                        </div>
                        {associateContractId === c.id && (
                          <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <Label className="text-sm">Razão * (mínimo 10 caracteres)</Label>
              <Textarea
                value={associateReason}
                onChange={(e) => setAssociateReason(e.target.value)}
                rows={3}
                placeholder="Ex: contrato criado diretamente pelo comercial sem passar pelo módulo de leads..."
                data-testid="associate-reason-textarea"
              />
              <p className={`text-xs mt-1 ${associateReason.trim().length < 10 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {associateReason.trim().length}/10
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssociateOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleAssociateContract}
              disabled={associating || !associateContractId || associateReason.trim().length < 10}
              className="bg-indigo-600 hover:bg-indigo-700 gap-2"
              data-testid="associate-submit-btn"
            >
              {associating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Associar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline dialog (shared pool) */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent data-testid="decline-dialog">
          <DialogHeader><DialogTitle>Descartar Lead do Pool</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Indica a razão (mínimo 10 caracteres). A lead vai deixar de te aparecer, mas continuará visível para os restantes elegíveis.
            </p>
            <div>
              <Label>Razão *</Label>
              <Textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={4}
                placeholder="Ex: Cliente fora da nossa área de cobertura..."
                data-testid="decline-reason-textarea"
              />
              <p className={`text-xs mt-1 ${declineReason.trim().length < 10 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {declineReason.trim().length}/10 caracteres mínimos
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleDecline}
              disabled={declining || declineReason.trim().length < 10}
              className="bg-rose-600 hover:bg-rose-700 gap-2"
              data-testid="decline-submit-btn"
            >
              {declining ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Lead Dialog (super_admin / gestor only) */}
      <Dialog open={reopenOpen} onOpenChange={(o) => { if (!o && !reopening) { setReopenOpen(false); setReopenReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="reopen-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Unlock className="w-5 h-5 text-rose-700" /> Reabrir Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Vais reabrir uma lead convertida. O <b>contract_id</b> será desligado e o estado volta para <b>"Em Contacto"</b>.
              O contrato existente <b>não</b> é eliminado — só perde a ligação à lead.
            </p>
            <p className="text-xs text-slate-500">O evento D+45 do calendário (se existir) será cancelado.</p>
            <div>
              <Label className="text-slate-600">Motivo *</Label>
              <Textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Ex: contrato cancelado pela comercializadora; cliente mudou de ideias..."
                rows={3}
                data-testid="reopen-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReopenOpen(false); setReopenReason(''); }} disabled={reopening}>Cancelar</Button>
            <Button
              onClick={handleReopenLead}
              disabled={reopening || !reopenReason.trim()}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-reopen-btn"
            >
              {reopening ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
              Reabrir Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default LeadDetailPage;
